import { Link } from 'react-router-dom';
import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { brandValues, timelineData, testimonials, faqData } from '@/mocks/homepage';
import { WebPageSchema, BreadcrumbListSchema, FAQPageSchema, OrganizationSchema } from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMsg('留言发送成功！');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('idle');
    setFormMsg('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value as string);
        }
      });

      const res = await fetch('http://cgbast.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* JSON parse failure, use raw responseText */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('留言发送成功！我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormMsg('留言发送成功！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMsg(typeof serverMsg === 'string' ? serverMsg : '留言发送失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试');
    }

    setTimeout(() => setFormStatus('idle'), 4000);
  }, []);
  const breadcrumbItems = [
    { name: '首页', url: 'http://cgbast.cn/' },
    { name: '关于我们', url: 'http://cgbast.cn/about' },
  ];

  return (
    <>
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 每日大赛黑料网"
        description="了解每日大赛黑料网的品牌故事、使命愿景和联系方式。一手爆料，吃瓜不停。"
        url="http://cgbast.cn/about"
      />
      <BreadcrumbListSchema items={breadcrumbItems} />
      <FAQPageSchema questions={faqData} />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex flex-col items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20newsroom%20with%20journalists%20working%20at%20desks%2C%20warm%20golden%20hour%20lighting%20through%20large%20windows%2C%20dramatic%20cinematic%20atmosphere%2C%20editorial%20office%20photography%20with%20soft%20shadows%2C%20professional%20yet%20warm%20vibe%2C%20high%20detail&width=1920&height=1100&seq=about-hero-bg&orientation=landscape"
              alt="每日大赛黑料网 - 关于我们"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          </div>

          <div className="relative z-10 w-full max-w-[900px] mx-auto px-6 flex flex-col items-center text-center pt-20 pb-10">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight">
              关于每日大赛黑料网
            </h1>
            <p className="text-background-50/80 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
              一手爆料，吃瓜不停 —— 我们致力于为吃瓜群众提供最新最全的娱乐八卦资讯
            </p>
          </div>

          {/* Timeline Cards */}
          <div className="relative z-10 w-full max-w-[900px] mx-auto px-6 pb-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {timelineData.map((item) => (
                <div
                  key={item.title}
                  className="bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-xl px-4 py-4 text-center"
                >
                  <span className="text-accent-400 text-xs font-bold">{item.date}</span>
                  <p className="text-background-50 font-semibold text-sm mt-1">{item.title}</p>
                  <p className="text-background-50/60 text-xs mt-0.5">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-50">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 py-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* About - Brand Philosophy */}
        <section id="story" className="py-16 md:py-24 bg-foreground-950">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 flex flex-col items-center text-center">
            <span className="text-foreground-400 text-xs tracking-widest uppercase font-medium mb-6">
              品牌故事
            </span>
            <h2 className="font-heading text-background-50 font-black text-2xl md:text-4xl lg:text-5xl leading-tight max-w-3xl">
              做最有态度的吃瓜平台
            </h2>
            <p className="text-foreground-400 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
              我们不煽风点火，我们只呈现真相
            </p>

            <div className="flex items-center gap-4 mt-10">
              <a
                href="#subscribe"
                className="px-6 py-2.5 bg-background-50 text-foreground-900 font-medium text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                加入我们
              </a>
              <a
                href="#reviews"
                className="px-6 py-2.5 border border-foreground-700 text-background-50 font-medium text-sm rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看口碑
              </a>
            </div>

            {/* Feature cards */}
            <div className="w-full grid grid-cols-2 sm:grid-cols-5 gap-3 mt-16">
              {brandValues.map((val) => (
                <div
                  key={val.label}
                  className="bg-foreground-800/60 rounded-xl px-4 py-4 flex flex-col items-center text-center gap-2"
                >
                  <i className={`${val.icon} text-primary-400 text-xl`}></i>
                  <span className="text-foreground-300 text-xs whitespace-nowrap">{val.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Detailed brand intro */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-foreground-900 font-bold text-xl font-heading mb-3">我们的使命</h3>
                <p className="text-foreground-600 leading-relaxed text-base">
                  每日大赛黑料网诞生于2026年，由一群热爱娱乐八卦、追求信息真实的内容创作者共同打造。我们看到了市场上充斥着大量未经核实的虚假爆料和煽风点火的标题党内容，决定创建一个有态度、有底线的吃瓜平台。
                </p>
              </div>
              <div>
                <h3 className="text-foreground-900 font-bold text-xl font-heading mb-3">我们的原则</h3>
                <p className="text-foreground-600 leading-relaxed text-base">
                  我们坚持"真实、客观、有趣"的内容原则。每一条爆料都经过多轮核实，拒绝恶意造谣和人身攻击。我们相信吃瓜是一种文化，更是一种责任。
                </p>
              </div>
              <div>
                <h3 className="text-foreground-900 font-bold text-xl font-heading mb-3">我们的团队</h3>
                <p className="text-foreground-600 leading-relaxed text-base">
                  我们的编辑团队来自一线媒体和互联网行业，拥有丰富的娱乐资讯采编经验。每位编辑都秉持专业精神，以最严谨的态度对待每一条爆料。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="reviews" className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-accent-600 text-sm font-medium mb-3">
                <i className="ri-chat-heart-line"></i>
                <span>用户口碑</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-foreground-900 font-heading">
                听听吃瓜群众<span className="text-accent-500">怎么说</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, idx) => {
                if (idx === 3) {
                  return (
                    <div
                      key={t.id}
                      className="relative rounded-2xl overflow-hidden aspect-[3/2] md:col-span-2"
                    >
                      <img
                        src="https://readdy.ai/api/search-image?query=Happy%20Chinese%20community%20of%20young%20people%20sharing%20phone%20content%20and%20laughing%20together%2C%20warm%20cozy%20atmosphere%2C%20golden%20lighting%2C%20lifestyle%20documentary%20photography&width=1000&height=600&seq=about-testimonial-wide&orientation=landscape"
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-background-50 font-semibold">{t.name}</p>
                        <p className="text-background-50/70 text-xs">{t.role}</p>
                        <p className="text-background-50/80 text-sm mt-2 leading-relaxed">{t.text}</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={t.id} className="bg-secondary-50 rounded-2xl p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-foreground-900 font-semibold text-sm">{t.name}</p>
                        <p className="text-foreground-500 text-xs">{t.role}</p>
                      </div>
                    </div>
                    <i className="ri-double-quotes-l text-secondary-300 text-2xl block mb-2"></i>
                    <p className="text-foreground-700 text-sm leading-relaxed">{t.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-900 font-heading mb-8 text-center">
              常见<span className="text-primary-500">问题</span>
            </h2>
            <div className="space-y-3">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="text-foreground-900 font-medium text-sm pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-black text-foreground-900 font-heading">
                联系<span className="text-primary-500">我们</span>
              </h2>
              <p className="text-foreground-500 text-sm mt-3 leading-relaxed">
                有爆料线索？有合作意向？欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-background-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-semibold text-sm mb-1">投稿邮箱</h3>
                <p className="text-foreground-500 text-xs">hello@cgbast.cn</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-wechat-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-semibold text-sm mb-1">微信公众号</h3>
                <p className="text-foreground-500 text-xs">每日大赛黑料网</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-secondary-600 text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-semibold text-sm mb-1">商务合作</h3>
                <p className="text-foreground-500 text-xs">hello@cgbast.cn</p>
              </div>
            </div>

            {/* Contact Form */}
            <div id="join" className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-foreground-900 font-bold text-lg font-heading mb-6">给我们留言</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-foreground-600 text-xs font-medium mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-foreground-600 text-xs font-medium mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="hello@cgbast.cn"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-foreground-600 text-xs font-medium mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-foreground-600 text-xs font-medium mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你的留言内容……"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                  <i className="ri-send-plane-line ml-1.5"></i>
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-xs mt-2 ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                    {formMsg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}